/**
 * PNG Metadata Embedder
 *
 * Injects tEXt chunks into PNG binary data so exported images carry
 * attribution metadata (author, software, source, copyright).
 *
 * PNG spec: tEXt chunks are keyword + null byte + value (Latin-1).
 * They must appear before the IEND chunk.
 */

/**
 * Embed metadata into a PNG data URL.
 * Returns a new data URL with tEXt chunks injected.
 *
 * @param {string} dataUrl - PNG data URL (data:image/png;base64,...)
 * @param {object} metadata - key/value pairs to embed
 * @returns {string} new data URL with metadata
 */
export function embedPngMetadata(dataUrl, metadata = {}) {
  const defaults = {
    'Software': 'stencil.cloud',
    'Author': 'stencil.cloud',
    'Source': 'https://stencil.cloud',
    'Copyright': 'Created with stencil.cloud',
    'Comment': 'Design exported from stencil.cloud — browser-based design kit',
  };

  const allMeta = { ...defaults, ...metadata };

  // Decode base64 to binary
  const base64 = dataUrl.split(',')[1];
  const binary = atob(base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  // Build tEXt chunks
  const chunks = [];
  for (const [key, value] of Object.entries(allMeta)) {
    chunks.push(createTextChunk(key, value));
  }

  // Find IEND position (last 12 bytes of a valid PNG)
  // IEND chunk: length(4) + "IEND"(4) + CRC(4) = 12 bytes at end
  const iendPos = findIEND(bytes);
  if (iendPos === -1) return dataUrl; // not a valid PNG, return as-is

  // Splice chunks before IEND
  const totalChunkSize = chunks.reduce((s, c) => s + c.length, 0);
  const result = new Uint8Array(bytes.length + totalChunkSize);

  // Copy everything before IEND
  result.set(bytes.subarray(0, iendPos), 0);

  // Insert metadata chunks
  let offset = iendPos;
  for (const chunk of chunks) {
    result.set(chunk, offset);
    offset += chunk.length;
  }

  // Copy IEND
  result.set(bytes.subarray(iendPos), offset);

  // Encode back to base64 data URL
  let binaryStr = '';
  for (let i = 0; i < result.length; i++) {
    binaryStr += String.fromCharCode(result[i]);
  }
  return 'data:image/png;base64,' + btoa(binaryStr);
}

/**
 * Create a PNG tEXt chunk.
 * Format: [4-byte length][4-byte type "tEXt"][keyword\0value][4-byte CRC]
 */
function createTextChunk(keyword, value) {
  const keyBytes = new TextEncoder().encode(keyword);
  const valBytes = new TextEncoder().encode(value);
  const dataLen = keyBytes.length + 1 + valBytes.length; // +1 for null separator

  const chunk = new Uint8Array(12 + dataLen); // 4 length + 4 type + data + 4 CRC
  const view = new DataView(chunk.buffer);

  // Length (big-endian)
  view.setUint32(0, dataLen);

  // Type: "tEXt"
  chunk[4] = 0x74; // t
  chunk[5] = 0x45; // E
  chunk[6] = 0x58; // X
  chunk[7] = 0x74; // t

  // Data: keyword + \0 + value
  chunk.set(keyBytes, 8);
  chunk[8 + keyBytes.length] = 0; // null separator
  chunk.set(valBytes, 8 + keyBytes.length + 1);

  // CRC over type + data
  const crc = crc32(chunk.subarray(4, 8 + dataLen));
  view.setUint32(8 + dataLen, crc);

  return chunk;
}

/**
 * Find the byte offset of the IEND chunk in PNG data.
 */
function findIEND(bytes) {
  // Search from end — IEND is always the last chunk
  for (let i = bytes.length - 8; i >= 8; i--) {
    if (bytes[i] === 0x49 && bytes[i + 1] === 0x45 &&
        bytes[i + 2] === 0x4E && bytes[i + 3] === 0x44) {
      return i - 4; // back up 4 bytes for the length field
    }
  }
  return -1;
}

/**
 * CRC-32 (PNG uses this for chunk integrity).
 */
const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
    }
    table[n] = c;
  }
  return table;
})();

function crc32(bytes) {
  let crc = 0xFFFFFFFF;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xFF] ^ (crc >>> 8);
  }
  return (crc ^ 0xFFFFFFFF) >>> 0;
}
